<template>
  <div data-test="wizard-sort-options">
    <translate tag="label" class="section-title"> Sort by </translate>
    <div class="sort-wrapper">
      <section ref="sort-option-wrapper" class="radio-group">
        <base-radio
          v-for="option of sortOptions"
          :key="option"
          ref="sort-options"
          :model-value="option"
          :value="option"
          :checked="option === radioSelectedOption"
          name="option"
          class="sort-radio-btn"
          @update:model-value="selectedChanged($event)"
        >
          <translate>{{ sortOptionsMap[option] }}</translate>
          <img
            v-if="option === ascendingOption"
            src="@/assets/images/ascending.svg"
          />
          <img
            v-if="option === descendingOption"
            src="@/assets/images/descending.svg"
          />
        </base-radio>
        <span class="pad" />
      </section>
      <transition name="fade">
        <translate
          v-if="radioSelectedOption === 'distance'"
          tag="p"
          class="distance-note"
        >
          All distances are calculated 'as the crow flies'.
        </translate>
      </transition>
    </div>
  </div>
</template>

<script lang="ts">
/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access */
import type BaseRadio from '~/components/base/BaseRadio.vue'
import {
  Component,
  Prop,
  Vue,
  Watch,
  toNative,
} from '~/utility/pu-class-decorator'
import type { SortOptions } from '../../schemas'

@Component({
  emits: ['update'],
})
class FilterAndSortSortOptions extends Vue {
  @Prop()
  options: SortOptions[]

  @Prop()
  selected: string

  @Prop()
  panelOpen: boolean

  radioSelectedOption: SortOptions | null = null

  selectedChanged(selected: string) {
    this.radioSelectedOption = selected
    if (this.selected !== selected) {
      this.$emit('update', selected)
    }
  }

  @Watch('selected', { immediate: true })
  updateSelected() {
    this.radioSelectedOption = this.selected
  }

  $refs: {
    'sort-options': BaseRadio[]
    'sort-option-wrapper': HTMLDivElement
  }

  @Watch('panelOpen')
  async panelOpenChanged(newVal) {
    if (newVal) {
      const selected = this.$refs['sort-options']?.find((el) => {
        return el.value === this.selected
      })

      if (selected && this.$refs['sort-option-wrapper']) {
        await nextTick()
        const elementOffset = Math.round(
          (selected.$el as HTMLElement).offsetLeft - 16,
        )
        this.$refs['sort-option-wrapper'].scrollLeft = elementOffset
      }
    }
  }

  get sortOptions() {
    const keys = Object.keys(this.sortOptionsMap)
    if (this.options && this.options.length) {
      return this.options.filter((opt) => keys.includes(opt))
    }
    return keys
  }

  ascendingOption = 'price'

  descendingOption = '-price'

  sortOptionsMap = {
    '-rating': 'User rating',
    '-weighted_rating': 'User rating/most reviews',
    distance: 'Distance',
    magic: 'Our favourites',
    price: 'Price',
    '-price': 'Price',
  }
}

export default toNative(FilterAndSortSortOptions)
</script>

<style scoped>
img {
  height: 1em;
  width: auto;
  margin: 0 0 0 0.4em;
}

.section-title {
  padding: 1em 0 0.5em 1em;
  font-weight: bold;
  display: block;
}

.sort-wrapper {
  position: relative;
  overflow: hidden;
  background: white;
}

.sort-wrapper::after {
  background: linear-gradient(
    270deg,
    #fff 10%,
    hsla(0, 0%, 100%, 0.9) 20%,
    hsla(0, 0%, 100%, 0)
  );
  bottom: 0;
  content: '';
  display: block;
  height: 100%;
  left: auto;
  pointer-events: none;
  position: absolute;
  right: 0;
  top: 0;
  width: 3em;
  z-index: 1;
}

.radio-group {
  align-items: center;
  background: white;
  display: flex;
  flex-flow: nowrap;
  justify-content: flex-start;
  overflow-x: scroll;
  overflow-y: hidden;
  padding: 0.5em 0.6em 0.4em 0.6em;
  width: 100vw;
  position: relative;
  left: 0;
}

.radio-group::-webkit-scrollbar {
  display: none;
}

:deep(.sort-radio-btn) {
  -webkit-tap-highlight-color: transparent;
  -webkit-user-select: none;
  align-items: center;
  border-radius: 4em;
  border: 4px solid var(--colour-page-background);
  display: flex;
  flex-shrink: 0;
  font-weight: normal;
  margin: 0.2em;
  padding: 0.3em 0.5em;
  user-select: none;
  vertical-align: middle;
  white-space: nowrap;
}

.sort-radio-btn :deep(.radiobutton) {
  display: none;
}

:deep(.sort-radio-btn span) {
  align-items: center;
  color: var(--colour-text-default);
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  user-select: none;
}

.radio-group .pad {
  display: flex;
  flex: 0 0 2em;
}

:deep(.sort-radio-btn.checked) {
  border-color: var(--colour-light-green);
  background-color: var(--colour-pale-blue);
  font-weight: bold;
}

:deep(.sort-radio-btn.checked div) {
  color: var(--colour-text-default);
}

.distance-note {
  position: relative;
  z-index: 2;
  padding: 0 1em 0.5em;
  font-size: var(--small);
}
</style>
